import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	Autocomplete,
	TextField,
	Button,
	Alert,
} from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import { apiErrorMessage } from '../../../api/catalog';
import { ModalActions } from './styles';

interface AvailableMateria {
	id: number;
	nombre: string;
}

interface AgregarMateriaDialogProps {
	open: boolean;
	options: AvailableMateria[];
	claimedSubjectIds: number[];
	onClose: () => void;
	onConfirmar: (subjectId: number, materiaNombre: string) => Promise<void>;
}

const AgregarMateriaDialog: React.FC<AgregarMateriaDialogProps> = ({
	open,
	options,
	claimedSubjectIds,
	onClose,
	onConfirmar,
}) => {
	const [selected, setSelected] = useState<AvailableMateria | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) {
			setSelected(null);
			setError(null);
			setSaving(false);
		}
	}, [open]);

	const available = options.filter((materia) => !claimedSubjectIds.includes(materia.id));

	const handleConfirmar = async () => {
		if (!selected || saving) return;
		setSaving(true);
		setError(null);
		try {
			await onConfirmar(selected.id, selected.nombre);
			setSelected(null);
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (saving) return;
		setSelected(null);
		setError(null);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>Agregar materia</DialogTitle>

			<DialogContent>
				<DialogContentText>
					Selecciona una materia del catálogo del plantel. Al agregarla recibirás un código único
					que podrás compartir con tus alumnos.
				</DialogContentText>

				<Autocomplete
					options={available}
					getOptionLabel={(option) => option.nombre}
					value={selected}
					onChange={(_, value) => setSelected(value)}
					noOptionsText='No hay materias disponibles'
					renderInput={(params) => (
						<TextField
							{...params}
							label='Materia'
							placeholder='Busca por nombre de materia...'
							margin='normal'
							fullWidth
						/>
					)}
				/>
				{error && <Alert severity='error'>{error}</Alert>}
			</DialogContent>

			<ModalActions>
				<Button variant='outlined' onClick={handleClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant='contained'
					onClick={handleConfirmar}
					disabled={!selected || saving}
					startIcon={<AddRounded />}
				>
					{saving ? 'Agregando...' : 'Agregar materia'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default AgregarMateriaDialog;
